import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import Modal from '../components/Modal';
import RelatedLinks from '../components/RelatedLinks';
import { useModal } from '../components/useModal';

/**
 * Area — a city or region that facilities belong to.
 *
 * The old admin splits this into a read-only page and a separate `/edit` form,
 * with an Edit action navigating between them. One page here, like the provider
 * and facility ones: the fields are the form, and Edit is not an action because
 * you are already in it.
 *
 * `new` is the same page with nothing loaded, so the create form cannot drift
 * from the edit form — which is what a second file guarantees will eventually
 * happen.
 *
 * Delete is real and irreversible, and the old page guards it with
 * `window.confirm`. It gets the danger dialog, naming the area.
 */

const AreaInternal = ({ api }) => {
  const { areaId } = useParams();
  const navigate = useNavigate();
  const { ask, modalProps } = useModal();

  const isNew = !areaId;

  const empty = {
    Name: '', Slug: '', CountryCode: '', Latitude: '', Longitude: '',
    ZipFrom: '', ZipTo: '', IsMainArea: false, IsSuperArea: false
  };

  const [area, setArea] = useState(isNew ? {} : null);
  const [form, setForm] = useState(isNew ? empty : null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  // The payload is camelCase here, unlike most of this admin.
  const toForm = data => ({
    Name: data.name || '',
    Slug: data.slug || '',
    CountryCode: data.countryCode || '',
    Latitude: data.position?.latitude ?? '',
    Longitude: data.position?.longitude ?? '',
    ZipFrom: data.zipFrom ?? '',
    ZipTo: data.zipTo ?? '',
    IsMainArea: !!data.isMainArea,
    IsSuperArea: !!data.isSuperArea
  });

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const data = await api.areas.getArea(areaId);
      if (!data || data.success === false || !(data.areaId || data.AreaId)) {
        setFailed(true);
        return;
      }
      setArea(data);
      setForm(toForm(data));
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, areaId, isNew]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/areas">← All areas</BackToList>
        <div className="next-note next-note--error">
          Could not load this area.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!form) return <div className="next-page next-muted">Loading…</div>;

  const set = (name, value) => setForm(previous => ({ ...previous, [name]: value }));

  const pristine = isNew ? empty : toForm(area);
  const dirty = Object.keys(form).some(key => form[key] !== pristine[key]);

  const number = (value, label) =>
    (value === '' || Number.isFinite(Number(value)) ? null : `${label} must be a number.`);

  const errors = {
    Name: form.Name.trim() ? null : 'Name is required.',
    Slug: form.Slug.trim() ? null : 'Slug is required.',
    Latitude: number(form.Latitude, 'Latitude'),
    Longitude: number(form.Longitude, 'Longitude'),
    ZipFrom: number(form.ZipFrom, 'Zip from'),
    ZipTo: number(form.ZipTo, 'Zip to')
  };
  const invalid = Object.values(errors).some(Boolean);

  const save = async event => {
    event.preventDefault();
    if (invalid || (!dirty && !isNew) || busy) return;

    // The old form's payload, including the empty-to-null and the uppercasing.
    const payload = {
      Name: form.Name.trim(),
      Slug: form.Slug.trim(),
      CountryCode: form.CountryCode.trim().toUpperCase(),
      Latitude: form.Latitude === '' ? null : parseFloat(form.Latitude),
      Longitude: form.Longitude === '' ? null : parseFloat(form.Longitude),
      ZipFrom: form.ZipFrom === '' ? null : parseInt(form.ZipFrom, 10),
      ZipTo: form.ZipTo === '' ? null : parseInt(form.ZipTo, 10),
      IsMainArea: form.IsMainArea,
      IsSuperArea: form.IsSuperArea
    };

    setBusy(true);
    setNote(null);
    try {
      const result = isNew
        ? await api.areas.createArea(payload)
        : await api.areas.updateArea(areaId, payload);

      if (result?.success === false) {
        setNote({ tone: 'error', text: result.errorMessage || 'The area was not saved.' });
        return;
      }

      const newId = result?.areaId || result?.AreaId;
      if (isNew && newId) {
        navigate(`/next/area/${newId}`);
        return;
      }
      await load();
      setNote({ tone: 'info', text: 'Area saved.' });
    } catch (error) {
      setNote({
        tone: 'error',
        text: error?.payload?.translatedMessage || error?.payload?.message
          || error?.response?.data?.message || error?.message || 'Unable to save the area.'
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = () => ask({
    kind: 'danger',
    title: `Delete ${form.Name || 'this area'}?`,
    context: `Delete area · ${form.Slug || areaId}`,
    body: 'The area is removed. Facilities that point at it lose their area, and any public page built on its slug stops resolving.',
    warning: 'This cannot be undone from the admin.',
    confirmLabel: 'Delete area',
    onConfirm: async () => {
      try {
        await api.areas.deleteArea(areaId);
      } catch (error) {
        setNote({
          tone: 'error',
          text: error?.response?.data?.message || error?.message || 'Unable to delete the area.'
        });
        throw error;
      }
      navigate('/next/areas');
    }
  });

  const field = (name, label, { hint } = {}) => (
    <label className="next-filter" key={name}>
      <span className="next-filter__label">{label}</span>
      <input
        className="next-input"
        value={form[name]}
        onChange={event => set(name, event.target.value)}
      />
      {(errors[name] || hint) && (
        <span className={errors[name] ? 'next-form__error' : 'next-form__hint'}>
          {errors[name] || hint}
        </span>
      )}
    </label>
  );

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/areas">← All areas</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{isNew ? 'New area' : (area.name || 'Area')}</h1>
          {form.IsMainArea && <span className="next-pill next-pill--info">Main area</span>}
          {form.IsSuperArea && <span className="next-pill next-pill--neutral">Super area</span>}
        </div>
        {!isNew && <span className="next-detail__id">{area.areaId || areaId}</span>}
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <form onSubmit={save}>
            <section className="next-card">
              <div className="next-card__head"><h2>Area</h2></div>
              <div className="next-form__grid">
                {field('Name', 'Name')}
                {field('Slug', 'Slug', { hint: 'Used in the public URL.' })}
                {field('CountryCode', 'Country code', { hint: 'Saved uppercase, e.g. DNK.' })}
                {field('Latitude', 'Latitude')}
                {field('Longitude', 'Longitude')}
                {field('ZipFrom', 'Zip from')}
                {field('ZipTo', 'Zip to')}

                <label className="next-filter next-filter--check">
                  <input
                    type="checkbox"
                    checked={form.IsMainArea}
                    onChange={event => set('IsMainArea', event.target.checked)}
                  />
                  <span>Main area</span>
                </label>

                <label className="next-filter next-filter--check">
                  <input
                    type="checkbox"
                    checked={form.IsSuperArea}
                    onChange={event => set('IsSuperArea', event.target.checked)}
                  />
                  <span>Super area</span>
                </label>
              </div>

              <div className="next-form__actions">
                <button
                  type="submit"
                  className="next-btn next-btn--primary"
                  disabled={invalid || (!dirty && !isNew) || busy}
                  title={invalid ? 'Some fields need fixing first' : (!dirty && !isNew ? 'Nothing has changed' : undefined)}
                >
                  {busy ? 'Saving…' : (isNew ? 'Create area' : 'Save changes')}
                </button>
                {!isNew && (
                  <button
                    type="button"
                    className="next-btn"
                    disabled={!dirty || busy}
                    onClick={() => setForm(toForm(area))}
                  >
                    Discard
                  </button>
                )}
                {dirty && !isNew && <span className="next-muted">Unsaved changes</span>}
              </div>
            </section>

            {!isNew && (
              <section className="next-card">
                <div className="next-card__head"><h2>Read only</h2></div>
                <div className="next-facts">
                  <div className="next-fact">
                    <span className="next-fact__label">Id</span>
                    <span className="next-fact__value"><Guid value={area.areaId || areaId} /></span>
                  </div>
                </div>
              </section>
            )}
          </form>
        </div>

        {!isNew && (
          <aside className="next-detail__side">
            <section className="next-card">
              <div className="next-card__head"><h2>Actions</h2></div>
              <div className="next-actionlist">
                <button
                  type="button"
                  className="next-actionlist__item next-actionlist__item--danger"
                  onClick={remove}
                >
                  Delete area
                </button>
              </div>
            </section>

            <section className="next-card">
              <div className="next-card__head"><h2>Links</h2></div>
              <RelatedLinks
                items={[
                  { label: 'Facilities', hint: 'in this area', to: `/next/facilities?areaId=${area.areaId || areaId}` },
                  form.CountryCode && {
                    label: 'Areas in this country',
                    hint: form.CountryCode,
                    to: `/next/areas?countryCode=${form.CountryCode}`
                  }
                ]}
              />
            </section>
          </aside>
        )}
      </div>

      <Modal {...modalProps} />
    </div>
  );
};

AreaInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Area = withApi(AreaInternal);

Area.requiresLogin = true;

export default Area;
